export type DeepPartial<T> = T extends Record<string, unknown>
    ? {
          [P in keyof T]?: DeepPartial<T[P]>;
      }
    : T;

function isArray(item: unknown): item is unknown[] {
    return Array.isArray(item);
}

function isObject(item: unknown): item is Record<string, unknown> {
    return (
        item !== null &&
        typeof item === 'object' &&
        !Array.isArray(item) &&
        !(item instanceof Date) &&
        typeof item !== 'function'
    );
}

export function mergeArray<T extends unknown[]>(a: T = [] as unknown as T, b: T = [] as unknown as T): T {
    const mapItem = (item: T) => {
        if (isObject(item)) {
            return mergeDeep({}, item) as T;
        }
        return item;
    };
    return [...a.map(mapItem), ...b.map(mapItem)] as T;
}

/**
 * Deeply merges two javascript objects. With support to json data types (arrays, objects, strings, numbers, booleans, null), Date instances and functions (which are copied).
 * Array merging is done by concatenating the arrays. If the arrays contain objects, and should be merged, it should be done aftwards (e.g: checking a key property do identify copies and merge).
 *
 * @param target object to be overwritten
 * @param source object with new data
 * @returns
 */
export function mergeDeep<
    T extends Record<string, unknown>,
    Q extends DeepPartial<T> = DeepPartial<T>,
    K extends keyof T & keyof Q = keyof T & keyof Q
>(target: T = {} as T, source: Q = {} as Q): T {
    const newObj = {} as T;
    const targetKeys = Object.keys(target) as K[];
    const sourceKeys = Object.keys(source) as K[];
    const allKeys = new Set([...targetKeys, ...sourceKeys]);

    const getTargetValue = <R>(key: K) => target[key] as unknown as R;
    const getSourceValue = <R>(key: K) => source[key] as unknown as R;

    const mergeObject = (key: K) => {
        const tValue = getTargetValue<T[K]>(key);
        const sValue = getSourceValue<Q[K]>(key);

        if (
            typeof tValue === 'function' ||
            typeof sValue === 'function' ||
            tValue instanceof Date ||
            sValue instanceof Date
        ) {
            return sValue || tValue;
        }

        return mergeDeep(
            newObj[key] as unknown as Record<string, unknown>,
            source[key as K] as unknown as Record<string, unknown>
        );
    };

    allKeys.forEach(key => {
        const tValue = getTargetValue<T[K]>(key);
        const sValue = getSourceValue<Q[K]>(key);

        if (isArray(sValue) || isArray(tValue)) {
            newObj[key] = mergeArray(
                target[key] as unknown as unknown[],
                source[key] as unknown as unknown[]
            ) as unknown as T[K];
            return;
        }

        newObj[key] = tValue;

        if (sValue) {
            const sourceValue = sValue as unknown as DeepPartial<T[K]>;

            if (isObject(sourceValue) || isObject(tValue)) {
                newObj[key] = mergeObject(key) as T[K];
            } else {
                newObj[key] = (sourceValue || tValue) as T[K];
            }
        } else if (isObject(tValue)) {
            newObj[key] = mergeDeep({}, tValue) as T[K];
        }
    });

    return newObj;
}
